import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface WebhookStats {
  totalToday: number;
  successRate: number;
  avgProcessingMs: number;
  errorsLast24h: number;
}

export function useWebhookStats() {
  return useQuery({
    queryKey: ['webhook-stats'],
    queryFn: async () => {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      
      // Fetch today's logs for stats
      const { data: todayLogs, error: e1 } = await supabase
        .from('webhook_logs')
        .select('status, processing_time_ms, is_dry_run')
        .gte('created_at', todayStart)
        .eq('is_dry_run', false);
      
      if (e1) throw e1;
      
      // Errors in last 24h
      const { count: errorsCount, error: e2 } = await supabase
        .from('webhook_logs')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'error')
        .eq('is_dry_run', false)
        .gte('created_at', last24h);
      
      if (e2) throw e2;
      
      const logs = todayLogs || [];
      const totalToday = logs.length;
      const successCount = logs.filter(l => l.status === 'success').length;
      const successRate = totalToday > 0 ? Math.round((successCount / totalToday) * 100) : 100;
      
      const timings = logs
        .map(l => l.processing_time_ms)
        .filter((t): t is number => t !== null && t > 0);
      const avgProcessingMs = timings.length > 0
        ? Math.round(timings.reduce((a, b) => a + b, 0) / timings.length)
        : 0;
      
      return {
        totalToday,
        successRate,
        avgProcessingMs,
        errorsLast24h: errorsCount || 0,
      } as WebhookStats;
    },
    refetchInterval: 60000,
  });
}
